import { Button, Flex, Icon, styled, Table, Text, Tooltip } from '@conteg/ui';
import {
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import { DebouncedSearchInput } from 'components/debounced-search-input/debounced-search-input';
import { usePolicyEvaluate } from 'components/policy-loader/policy-loader';
import StatusBadge from 'components/status-badge/status-badge';
import StorageUnitFeaturesCompact from 'components/storage-unit-features-compact/storage-unit-features-compact';
import {
  IS_KIOSK,
  PACKAGE_DETAIL_EXTERNAL_ROUTE,
  STORAGE_UNIT_HISTORY_EXTERNAL_ROUTE,
} from 'config';
import { getEmails } from 'pages/storage-unit-detail/utils/get-emails';
import { getPhoneNumbers } from 'pages/storage-unit-detail/utils/get-phone-numbers';
import DataGridFilter, {
  StorageUnitFilterOptionsEnum,
  StorageUnitFilterValues,
} from 'pages/storage-unit-list/components/data-grid/data-grid-filter';
import { sortByEmpty } from 'pages/storage-unit-list/components/sort-by-is-empty';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  LockStatus,
  StorageUnitFeature,
  StorageUnitListFragment,
  StorageUnitStatus,
} from 'types/generated/graphql';
import { useDeviceStore } from 'utils/device/device-store';
import { getStatusTranslation } from 'utils/localization/get-status-translation';
import { openExternalLink } from 'utils/open-external-link/open-external-link';
import { appRoutes, substituteRouteParams } from 'utils/routing/routes';
import { formatStorageUnitSize } from 'utils/storage-unit-size/format-storage-unit-size';
import { formatDateTime, TimeFormats } from 'utils/time/format-time';

import { isAllocationExpired } from '../../help-functions';
import StorageUnitActions from '../modal/storage-unit-actions';

type DataGridProps = {
  storageUnitList: StorageUnitListFragment[];
  isLoading: boolean;
};

const columnHelper = createColumnHelper<StorageUnitListFragment>();

const InputWrapper = styled.div`
  width: 120rem;

  input {
    height: 14rem;
  }
`;

const NeverEndingSymbol = styled(Text)`
  font-size: 5rem;
`;

const StyledAllocationTo = styled.span<{ expired: boolean }>`
  color: ${({ expired, theme }) =>
    expired ? theme.colors.additional.error : 'inherit'};
`;

const DataGrid = ({ storageUnitList, isLoading }: DataGridProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pointId = useDeviceStore((state) => state.pointId);

  const [storageUnitFilter, setStorageUnitFilter] =
    useState<StorageUnitFilterValues>(StorageUnitFilterOptionsEnum.ALL);

  const [tableData, setTableData] =
    useState<StorageUnitListFragment[]>(storageUnitList);

  const [sorting, setSorting] = useState<SortingState>([]);

  const [searchTerm, setSearchTerm] = useState<string>('');

  const canDisplayReservationDetail = usePolicyEvaluate(
    'displayReservationDetail',
    'maintenance'
  );

  const canDisplayReservationPreview = usePolicyEvaluate(
    'displayReservationPreview',
    'maintenance'
  );

  const canDisplayServiceUnitDetail = usePolicyEvaluate(
    'canDisplayServiceUnitDetail',
    'maintenance'
  );

  const canDisplayEmptyStorageUnitDetail = usePolicyEvaluate(
    'canDisplayEmptyStorageUnitDetail',
    'maintenance'
  );

  useEffect(() => {
    switch (storageUnitFilter) {
      case StorageUnitFilterOptionsEnum.ALL:
        setTableData(storageUnitList);
        break;
      case StorageUnitFilterOptionsEnum.ServiceModule:
        setTableData(
          storageUnitList.filter(
            (row) => row.storageUnit?.storageUnitSize?.isServiceModule
          )
        );
        break;
      case StorageUnitFilterOptionsEnum.EMPTY:
        setTableData(storageUnitList.filter((row) => row.isEmpty));
        break;
      case StorageUnitFilterOptionsEnum.OCCUPIED:
        setTableData(storageUnitList.filter((row) => !!row.reservationId));
        break;
      case LockStatus.Opened:
        setTableData(
          storageUnitList.filter(
            (row) => row.storageUnit?.lock?.lastStatus === LockStatus.Opened
          )
        );
        break;
      case StorageUnitStatus.Blocked:
        setTableData(
          storageUnitList.filter(
            (row) =>
              row.storageUnit?.storageUnitStatus === StorageUnitStatus.Blocked
          )
        );
        break;
      case StorageUnitStatus.Broken:
        setTableData(
          storageUnitList.filter(
            (row) =>
              row.storageUnit?.storageUnitStatus === StorageUnitStatus.Broken
          )
        );
        break;
      case StorageUnitFeature.AcSocket:
        setTableData(
          storageUnitList.filter((row) =>
            row.storageUnit?.features.includes(StorageUnitFeature.AcSocket)
          )
        );
        break;
      case StorageUnitFilterOptionsEnum.EXPIRED:
        setTableData(
          storageUnitList.filter((row) => isAllocationExpired(row.to))
        );
        break;
      case StorageUnitFeature.Cooling:
        setTableData(
          storageUnitList.filter((row) =>
            row.storageUnit?.features.includes(StorageUnitFeature.Cooling)
          )
        );
    }
  }, [storageUnitList, storageUnitFilter]);

  const storageUnitsColumns = [
    columnHelper.accessor((row) => row.storageUnit?.name, {
      id: 'name',
      cell: (info) => info.getValue(),
      header: () => (
        <span>{t('Page.StorageUnitList.Table.Header.BoxName')}</span>
      ),
      enableSorting: true,
    }),
    columnHelper.accessor(
      (row) => {
        const features = row.storageUnit?.features.length
          ? row.storageUnit.features
          : null;
        return (
          <StorageUnitFeaturesCompact
            storageUnitFeatures={features}
            isServiceModul={
              row.storageUnit.storageUnitSize?.isServiceModule ?? false
            }
          />
        );
      },
      {
        id: 'storageUnitFeatures',
        cell: (info) => info.getValue(),
        header: () => (
          <span>
            {t('Page.StorageUnitList.Table.Header.StorageUnitFeatures')}
          </span>
        ),
        enableSorting: false,
      }
    ),
    columnHelper.accessor(
      (row) => formatStorageUnitSize(row.storageUnit?.storageUnitSize),
      {
        id: 'size',
        cell: (info) => info.getValue(),
        header: () => (
          <span>{t('Page.StorageUnitList.Table.Header.Size')}</span>
        ),
        enableSorting: true,
      }
    ),
    columnHelper.accessor(
      (row) =>
        row.isEasyAccessible ? (
          <Tooltip
            testId="storage-unit-easy-accessible-yes"
            content={t('Page.StorageUnitList.Table.EasyAccessible.Yes')}
          >
            <Icon name="check" color="green" />
          </Tooltip>
        ) : (
          <Tooltip
            testId="storage-unit-easy-accessible-no"
            content={t('Page.StorageUnitList.Table.EasyAccessible.No')}
          >
            <Icon name="close" color="red" />
          </Tooltip>
        ),
      {
        id: 'easyAccessible',
        cell: (info) => info.getValue(),
        header: () => (
          <span>{t('Page.StorageUnitList.Table.Header.EasyAccessible')}</span>
        ),
        enableSorting: false,
      }
    ),
    columnHelper.accessor(
      (row) => {
        switch (row.storageUnit?.storageUnitStatus) {
          case StorageUnitStatus.Blocked:
            return (
              <Tooltip
                testId="storage-unit-status-tooltip-blocked"
                content={t('Page.StorageUnitList.Table.Active.Blocked')}
              >
                ⛔
              </Tooltip>
            );
          case StorageUnitStatus.Broken:
            return (
              <Tooltip
                testId="storage-unit-status-tooltip-broken"
                content={t('Page.StorageUnitList.Table.Active.Broken', {
                  reason: row.storageUnit?.statusChangeReason ?? '',
                })}
              >
                🔧
              </Tooltip>
            );
          default:
            return (
              <Tooltip
                testId="storage-unit-status-tooltip-usable"
                content={t('Page.StorageUnitList.Table.Active.Usable')}
              >
                ✅
              </Tooltip>
            );
        }
      },
      {
        id: 'active',
        cell: (info) => info.getValue(),
        header: () => (
          <span>{t('Page.StorageUnitList.Table.Header.Active')}</span>
        ),
        enableSorting: false,
      }
    ),
    columnHelper.accessor(
      (row) =>
        row.storageUnit?.lock?.lastStatus === LockStatus.Closed ? (
          <Tooltip
            testId="storage-unit-locked-status-tooltip-locked"
            content={t('Page.StorageUnitList.Table.Lock.Locked')}
          >
            🔒
          </Tooltip>
        ) : (
          <Tooltip
            testId="storage-unit-locked-status-tooltip-unlocked"
            content={t('Page.StorageUnitList.Table.Lock.Unlocked')}
          >
            ❌
          </Tooltip>
        ),
      {
        id: 'lock',
        cell: (info) => info.getValue(),
        header: () => (
          <span>{t('Page.StorageUnitList.Table.Header.Lock')}</span>
        ),
        enableSorting: false,
      }
    ),
    columnHelper.accessor((row) => row, {
      id: 'status',
      cell: (info) => {
        const unit = info.getValue();

        if (unit.storageUnit.storageUnitSize?.isServiceModule) {
          return null;
        }

        const expired = isAllocationExpired(unit.to);
        const translationKey = getStatusTranslation({
          isEmpty: unit.isEmpty,
          isExpired: expired,
          isBlocking: unit.isBlockingReservation,
          canDisplayReservationPreview: !!canDisplayReservationPreview,
          hasUpcomingReservation:
            !!unit.firstUpcomingReservation && !unit.reservationId,
        });

        return <StatusBadge title={translationKey} />;
      },
      header: () => (
        <span>{t('Page.StorageUnitList.Table.Header.Status')}</span>
      ),
      enableSorting: true,
      sortingFn: sortByEmpty,
      sortUndefined: false,
    }),
    columnHelper.accessor(
      (row) => {
        if (row.isNeverEndingReservation) {
          return <NeverEndingSymbol content="∞" variant="normal" />;
        }

        if (row.from && row.to && canDisplayReservationPreview) {
          const expired = isAllocationExpired(row.to);
          return (
            <>
              {formatDateTime(row.from, TimeFormats.dateTime)} -{' '}
              <StyledAllocationTo expired={expired}>
                {formatDateTime(row.to, TimeFormats.dateTime)}
              </StyledAllocationTo>
            </>
          );
        }

        return '';
      },
      {
        id: 'reservationPeriod',
        cell: (info) => info.getValue(),
        header: () => (
          <span>
            {t('Page.StorageUnitList.Table.Header.ReservationPeriod')}
          </span>
        ),
        enableSorting: false,
        sortingFn: 'datetime',
      }
    ),
    columnHelper.accessor(
      (row) =>
        canDisplayReservationPreview
          ? (getPhoneNumbers(row.customerPinCredentials, row.serviceType)
              .phoneNumber ?? '')
          : '',
      {
        id: 'phoneNumber',
        cell: (info) => info.getValue(),
        header: () => (
          <span>{t('Page.StorageUnitList.Table.Header.PhoneNumber')}</span>
        ),
        enableSorting: false,
      }
    ),
    columnHelper.accessor(
      (row) =>
        canDisplayReservationPreview
          ? (getEmails(row.customerPinCredentials, row.serviceType).email ?? '')
          : '',
      {
        id: 'email',
        cell: (info) => info.getValue(),
        header: () => (
          <span>{t('Page.StorageUnitList.Table.Header.Email')}</span>
        ),
        enableSorting: false,
      }
    ),
    columnHelper.accessor(
      (row) => (canDisplayReservationPreview ? row.contents : ''),
      {
        id: 'barcode',
        cell: (info) => {
          if (!Array.isArray(info.getValue())) {
            return '';
          }

          const contents =
            info.getValue() as StorageUnitListFragment['contents'];

          if (!contents.length) {
            return '';
          }

          if (contents.length === 1) {
            const identifiers =
              contents[0]?.trackingIdentifiers
                ?.map((identifier) => identifier.identifier)
                .join(', ') ?? '';
            return identifiers;
          }

          return t('Page.StorageUnitList.Table.TrackingIdentifier.Multiple', {
            firstIdentifier:
              contents[0]?.trackingIdentifiers?.[0]?.identifier ?? '',
          });
        },
        header: () => (
          <span>
            {t('Page.StorageUnitList.Table.Header.TrackingIdentifier')}
          </span>
        ),
        enableSorting: false,
      }
    ),
    columnHelper.accessor(
      (row) => {
        const storageUnitId = row.storageUnit?.id;

        const isServiceModule =
          row.storageUnit.storageUnitSize?.isServiceModule;

        const isForbiddenServiceUnit =
          isServiceModule && !canDisplayServiceUnitDetail;

        const isForbiddenReservationDetail =
          !!row.reservationId && !canDisplayReservationDetail;

        const isForbiddenToOpenEmptyStorageUnitDetail =
          !row.reservationId &&
          !canDisplayEmptyStorageUnitDetail &&
          !isServiceModule;

        const isOccupiedByDifferentTenant = !row.reservationId && !row.isEmpty;

        const isDetailButtonDisabled =
          isForbiddenReservationDetail ||
          isForbiddenServiceUnit ||
          isForbiddenToOpenEmptyStorageUnitDetail ||
          isOccupiedByDifferentTenant;

        const isStorageUnitHistoryButtonDisabled =
          isForbiddenReservationDetail ||
          isForbiddenServiceUnit ||
          isForbiddenToOpenEmptyStorageUnitDetail;

        return (
          <Flex gap="3rem">
            <Tooltip
              testId="storage-unit-detail-button-tooltip"
              content={t(
                'Page.StorageUnitList.Table.Detail.BlockedDetailButtonTooltip'
              )}
              isTooltipHidden={!isDetailButtonDisabled}
            >
              <Button
                disabled={isDetailButtonDisabled}
                testId={row.storageUnit?.id}
                title={t('Page.StorageUnitList.Table.Button.Details')}
                variant="primary"
                onClick={() =>
                  navigate(
                    substituteRouteParams(appRoutes.storageUnitDetail, {
                      storageUnitId: row.storageUnit?.id,
                    })
                  )
                }
              />
            </Tooltip>
            {storageUnitId && pointId && !IS_KIOSK && (
              <Button
                disabled={isStorageUnitHistoryButtonDisabled}
                title={t(
                  'Page.StorageUnitList.Table.Button.StorageUnitHistory'
                )}
                variant="primary"
                onClick={() =>
                  openExternalLink(
                    substituteRouteParams(STORAGE_UNIT_HISTORY_EXTERNAL_ROUTE, {
                      storageUnitId,
                      pointId,
                    })
                  )
                }
              />
            )}
            {row.reservationId &&
              row.contents?.length >= 1 &&
              canDisplayReservationDetail &&
              !IS_KIOSK && (
                <Button
                  disabled={isDetailButtonDisabled}
                  testId="storage-unit-history-button"
                  title={t('Page.StorageUnitList.Table.Button.PackageHistory')}
                  variant="primary"
                  onClick={() =>
                    openExternalLink(
                      substituteRouteParams(PACKAGE_DETAIL_EXTERNAL_ROUTE, {
                        trackingIdentifier:
                          row.contents[0]?.trackingIdentifiers?.[0]
                            ?.identifier ?? '',
                      })
                    )
                  }
                />
              )}
            <StorageUnitActions storageUnit={row} />
          </Flex>
        );
      },
      {
        id: 'actions',
        cell: (info) => info.getValue(),
        header: () => (
          <span>{t('Page.StorageUnitList.Table.Header.Actions')}</span>
        ),
        enableSorting: false,
      }
    ),
  ];

  const filteredData = useMemo(
    () =>
      searchTerm
        ? tableData.filter((item) => filterDataBySearchTerm(item, searchTerm))
        : tableData,
    [searchTerm, tableData]
  );

  const storageUnitsTable = useReactTable<StorageUnitListFragment>({
    data: filteredData ?? [],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      columnVisibility: { nextReservation: !IS_KIOSK },
      sorting,
    },
    columns: storageUnitsColumns,
  });

  return (
    <Flex flexDirection="column" gap="1rem">
      <Flex gap="5rem" alignItems="center">
        <InputWrapper>
          <DebouncedSearchInput
            label={t('Page.StorageUnitList.Filter.Input.Label')}
            onChange={setSearchTerm}
          />
        </InputWrapper>
        <DataGridFilter
          selectedStatus={storageUnitFilter}
          setStatus={setStorageUnitFilter}
        />
      </Flex>
      <Table
        testId="storage-unit-list-table"
        table={storageUnitsTable}
        isEmpty={!tableData.length}
        isLoading={isLoading}
        emptyState={{
          title: t('Page.StorageUnitList.Table.EmptyData.Title'),
          description: t('Page.StorageUnitList.Table.EmptyData.Description'),
        }}
        disableRow={(row) =>
          row.storageUnit?.storageUnitStatus !== StorageUnitStatus.Usable
        }
      />
    </Flex>
  );
};

export default DataGrid;

const filterDataBySearchTerm = (
  row: StorageUnitListFragment,
  searchTerm: string
) => {
  if (!searchTerm) {
    return true;
  }

  const formattedSearchTerm = searchTerm.toLowerCase().replace(' ', '');

  const formattedTrackingIdentifiers =
    row.contents
      ?.flatMap(
        (content) =>
          content.trackingIdentifiers?.map((ti) => ti.identifier) ?? []
      )
      .join('') ?? '';

  const { phoneNumber } = getPhoneNumbers(
    row.customerPinCredentials,
    row.serviceType
  );
  const { email } = getEmails(row.customerPinCredentials, row.serviceType);

  const formattedRow =
    `${formattedTrackingIdentifiers}${phoneNumber ?? ''}${email ?? ''}`
      .replace(' ', '')
      .toLowerCase();

  return formattedRow.includes(formattedSearchTerm);
};
